import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNotification } from '../../contexts/NotificationContext';
import api from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import { countries } from '../../utils/countries';

const initialFormData = {
  productSku: '',
  productName: '',
  internalSku: '',
  alternatePartNumber: '',
  description: '',
  vendor: '',
  packagingType: '',
  unitOfMeasurement: 'Each',
  length: '',
  width: '',
  height: '',
  weightLbs: '',
  cartonLength: '',
  cartonWidth: '',
  cartonHeight: '',
  cartonWeight: '',
  cartonUnitOfMeasurement: 'Each',
  unitsPerCarton: '',
  value: '',
  upc: '',
  hsCode: '',
  countryOfOrigin: '',
  notes: ''
};

const CustomerInventoryForm = ({ onClose, onSuccess }) => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  const createInventoryMutation = useMutation(
    (data) => api.post('/inventory', data),
    {
      onSuccess: (response) => {
        showSuccess('Product created successfully');
        queryClient.invalidateQueries('customer-inventory');
        queryClient.invalidateQueries('customerInventory');
        onSuccess && onSuccess(response.data);
        onClose();
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to create product';
        showError(message);
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.productSku.trim()) {
      newErrors.productSku = 'Product SKU is required';
    }

    if (!formData.productName.trim()) {
      newErrors.productName = 'Product name is required';
    }

    if (!formData.packagingType) {
      newErrors.packagingType = 'Packaging type is required';
    }

    if (formData.packagingType === 'carton' && !formData.unitsPerCarton) {
      newErrors.unitsPerCarton = 'Units per carton is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    createInventoryMutation.mutate({
      productSku: formData.productSku.trim(),
      productName: formData.productName.trim(),
      internalSku: formData.internalSku.trim() || null,
      alternatePartNumber: formData.alternatePartNumber.trim() || null,
      description: formData.description.trim() || null,
      vendor: formData.vendor.trim() || null,
      packagingType: formData.packagingType,
      unitOfMeasurement: formData.unitOfMeasurement || null,
      individualDimensions: {
        length: formData.length ? parseFloat(formData.length) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        weight: formData.weightLbs ? parseFloat(formData.weightLbs) : null
      },
      cartonDimensions: {
        length: formData.cartonLength ? parseFloat(formData.cartonLength) : null,
        width: formData.cartonWidth ? parseFloat(formData.cartonWidth) : null,
        height: formData.cartonHeight ? parseFloat(formData.cartonHeight) : null,
        weight: formData.cartonWeight ? parseFloat(formData.cartonWeight) : null
      },
      cartonUom: formData.cartonUnitOfMeasurement || null,
      unitsPerCarton: formData.unitsPerCarton ? parseInt(formData.unitsPerCarton, 10) : null,
      value: formData.value ? parseFloat(formData.value) : null,
      upc: formData.upc.trim() || null,
      hsCode: formData.hsCode.trim() || null,
      countryOfOrigin: formData.countryOfOrigin || null,
      notes: formData.notes.trim() || null
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Add New Product</h1>
          <p className="mt-2 text-sm text-gray-600">Create a new inventory catalog entry.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="productSku" className="mb-2 block text-sm font-medium text-gray-700">
                Product SKU *
              </label>
              <input
                id="productSku"
                name="productSku"
                type="text"
                value={formData.productSku}
                onChange={handleChange}
                className={`w-full rounded-md border px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 ${
                  errors.productSku ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.productSku && <p className="mt-1 text-sm text-red-600">{errors.productSku}</p>}
            </div>

            <div>
              <label htmlFor="productName" className="mb-2 block text-sm font-medium text-gray-700">
                Product Name *
              </label>
              <input
                id="productName"
                name="productName"
                type="text"
                value={formData.productName}
                onChange={handleChange}
                className={`w-full rounded-md border px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 ${
                  errors.productName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.productName && <p className="mt-1 text-sm text-red-600">{errors.productName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="internalSku" className="mb-2 block text-sm font-medium text-gray-700">
                Internal SKU (Optional)
              </label>
              <input
                id="internalSku"
                name="internalSku"
                type="text"
                value={formData.internalSku}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="alternatePartNumber" className="mb-2 block text-sm font-medium text-gray-700">
                Manufacturer SKU (Optional)
              </label>
              <input
                id="alternatePartNumber"
                name="alternatePartNumber"
                type="text"
                value={formData.alternatePartNumber}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor="description" className="mb-2 block text-sm font-medium text-gray-700">
              Product Description (Optional)
            </label>
            <textarea
              id="description"
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="vendor" className="mb-2 block text-sm font-medium text-gray-700">
              Vendor
            </label>
            <input
              id="vendor"
              name="vendor"
              type="text"
              value={formData.vendor}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="packagingType" className="mb-2 block text-sm font-medium text-gray-700">
                Packaging Type *
              </label>
              <select
                id="packagingType"
                name="packagingType"
                value={formData.packagingType}
                onChange={handleChange}
                className={`w-full rounded-md border px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 ${
                  errors.packagingType ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Packaging Type</option>
                <option value="individual">Individual Units</option>
                <option value="carton">Carton</option>
              </select>
              {errors.packagingType && <p className="mt-1 text-sm text-red-600">{errors.packagingType}</p>}
            </div>

            <div>
              <label htmlFor="unitOfMeasurement" className="mb-2 block text-sm font-medium text-gray-700">
                Unit of Measurement
              </label>
              <select
                id="unitOfMeasurement"
                name="unitOfMeasurement"
                value={formData.unitOfMeasurement}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              >
                <option value="Each">Each</option>
                <option value="Box">Box</option>
                <option value="Pallet">Pallet</option>
              </select>
            </div>
          </div>

          {formData.packagingType === 'carton' && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-sm font-semibold text-gray-900">Carton Details</h4>
              <div className="mt-4 space-y-6">
                <div>
                  <label htmlFor="cartonWeight" className="mb-2 block text-sm font-medium text-gray-700">
                    Carton Weight (lbs)
                  </label>
                  <input
                    id="cartonWeight"
                    name="cartonWeight"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cartonWeight}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label htmlFor="cartonLength" className="mb-2 block text-sm font-medium text-gray-700">
                      Carton Length (inches)
                    </label>
                    <input
                      id="cartonLength"
                      name="cartonLength"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.cartonLength}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="cartonWidth" className="mb-2 block text-sm font-medium text-gray-700">
                      Carton Width (inches)
                    </label>
                    <input
                      id="cartonWidth"
                      name="cartonWidth"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.cartonWidth}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="cartonHeight" className="mb-2 block text-sm font-medium text-gray-700">
                      Carton Height (inches)
                    </label>
                    <input
                      id="cartonHeight"
                      name="cartonHeight"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.cartonHeight}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="cartonUnitOfMeasurement" className="mb-2 block text-sm font-medium text-gray-700">
                      Carton UOM
                    </label>
                    <select
                      id="cartonUnitOfMeasurement"
                      name="cartonUnitOfMeasurement"
                      value={formData.cartonUnitOfMeasurement}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    >
                      <option value="Each">Each</option>
                      <option value="Case">Case</option>
                      <option value="Pack">Pack</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="unitsPerCarton" className="mb-2 block text-sm font-medium text-gray-700">
                      Qty per Carton
                    </label>
                    <input
                      id="unitsPerCarton"
                      name="unitsPerCarton"
                      type="number"
                      min="1"
                      value={formData.unitsPerCarton}
                      onChange={handleChange}
                      className={`w-full rounded-md border px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 ${
                        errors.unitsPerCarton ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.unitsPerCarton && <p className="mt-1 text-sm text-red-600">{errors.unitsPerCarton}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {formData.packagingType && (
            <div className="rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-900">Item Details</h4>
              <div className="mt-4 space-y-6">
                <div>
                  <label htmlFor="weightLbs" className="mb-2 block text-sm font-medium text-gray-700">
                    Item Weight (lbs)
                  </label>
                  <input
                    id="weightLbs"
                    name="weightLbs"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.weightLbs}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label htmlFor="length" className="mb-2 block text-sm font-medium text-gray-700">
                      Item Length (inches)
                    </label>
                    <input
                      id="length"
                      name="length"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.length}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="width" className="mb-2 block text-sm font-medium text-gray-700">
                      Item Width (inches)
                    </label>
                    <input
                      id="width"
                      name="width"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.width}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="height" className="mb-2 block text-sm font-medium text-gray-700">
                      Item Height (inches)
                    </label>
                    <input
                      id="height"
                      name="height"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.height}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="value" className="mb-2 block text-sm font-medium text-gray-700">
                  Approximate Value ($) (per item)
              </label>
              <input
                id="value"
                name="value"
                type="number"
                step="0.01"
                min="0"
                value={formData.value}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="upc" className="mb-2 block text-sm font-medium text-gray-700">
                UPC / EAN / GTIN
              </label>
              <input
                id="upc"
                name="upc"
                type="text"
                value={formData.upc}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor="hsCode" className="mb-2 block text-sm font-medium text-gray-700">
              HS Code
            </label>
            <input
              id="hsCode"
              name="hsCode"
              type="text"
              value={formData.hsCode}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="countryOfOrigin" className="mb-2 block text-sm font-medium text-gray-700">
              Country of Origin
            </label>
            <select
              id="countryOfOrigin"
              name="countryOfOrigin"
              value={formData.countryOfOrigin}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.code} value={country.name}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="notes" className="mb-2 block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              rows={4}
              value={formData.notes}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            />
          </div>

          <div className="flex justify-end space-x-4 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createInventoryMutation.isLoading}
              className="flex items-center rounded-md border border-transparent bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createInventoryMutation.isLoading && <LoadingSpinner size="sm" className="mr-2" />}
              {createInventoryMutation.isLoading ? 'Creating...' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerInventoryForm;
